<template>
  <div class="relative">
    <Toast position="top-center" />
    <button
      class="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
      @click="togglePopup"
    >
      <i class="pi pi-bell text-xl" :class="iconClass"></i>
      <span
        v-if="unreadCount > 0"
        class="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>

    <Popover ref="popover" @show="onPopoverShow">
      <div class="w-[calc(100vw-2rem)] sm:w-96 max-h-96 overflow-hidden">
        <div
          class="flex items-center justify-between pb-1 border-b border-gray-200"
        >
          <h3 class="text-lg font-semibold">Notificări</h3>
          <button
            v-if="unreadCount > 0"
            class="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
            @click="markAllAsRead"
          >
            Marchează toate ca citite
          </button>
        </div>
        <div class="overflow-y-auto max-h-80">
          <div
            v-if="notifications.length === 0"
            class="p-4 text-center text-gray-500"
          >
            Nu există notificări
          </div>
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="p-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
            :class="{ 'bg-blue-50': !notification.seen }"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex justify-between items-start mb-1">
              <p class="text-sm text-gray-800">
                <template v-if="isFeedbackFile(notification)">
                  <span class="font-semibold">{{
                    feedbackParts(notification).who
                  }}</span>
                  a încărcat {{ filesCountLabel(notification) }} in
                  <span class="font-semibold">{{
                    feedbackParts(notification).subject
                  }}</span>
                  (
                  <span class="font-semibold">{{
                    feedbackParts(notification).course
                  }}</span>
                  ).
                </template>
                <template v-else-if="isTestPassed(notification)">
                  <span class="font-semibold">{{
                    notification.trigger_user_name
                  }}</span>
                  a finalizat evaluarea
                  <span class="font-semibold">{{
                    notification.subject_name
                  }}</span>
                  cu
                  <span
                    :class="
                      quizResultPassed(
                        notification.correct_answers_count,
                        notification.questions_count,
                      )
                        ? 'font-bold text-green-600'
                        : 'font-bold text-red-600'
                    "
                  >
                    {{
                      quizResultPercentage(
                        notification.correct_answers_count,
                        notification.questions_count,
                      )
                    }}% </span
                  >.
                </template>
                <template v-else-if="isExamAllocated(notification)">
                  Ti-a fost alocat un examen:
                  <span class="font-semibold">{{
                    notification.subject_name
                  }}</span>
                  (<span class="font-semibold">{{
                    notification.course_name
                  }}</span
                  >).
                </template>
                <template v-else>
                  {{ fallbackMessage(notification) }}
                </template>
              </p>
              <i
                v-if="!notification.seen"
                class="pi pi-circle-fill text-blue-500 text-xs ml-2"
              ></i>
            </div>
            <p class="text-xs text-gray-500">
              {{ formatDate(notification.created_at) }}
            </p>
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script>
import * as timeago from 'timeago.js';
import Popover from 'primevue/popover';
import Toast from 'primevue/toast';
import notificationsService from '@/services/notificationsService';
import { quizResultPercentage, quizResultPassed } from '@/utils/formatters';

export default {
  name: 'NotificationsPopup',
  components: {
    Popover,
    Toast,
  },
  props: {
    iconClass: {
      type: String,
      default: 'text-gray-700',
    },
  },
  data() {
    return {
      notificationsState: notificationsService.state,
    };
  },
  computed: {
    notifications() {
      return this.notificationsState.notifications;
    },
    unreadCount() {
      return notificationsService.unreadCount.value;
    },
  },
  methods: {
    quizResultPercentage,
    quizResultPassed,
    togglePopup(event) {
      this.$refs.popover.toggle(event);
    },
    onPopoverShow() {
      notificationsService.fetchNotifications();
    },
    async markAllAsRead() {
      try {
        await notificationsService.markAllAsSeen();
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    },
    async handleNotificationClick(notification) {
      if (!notification.seen) {
        try {
          await notificationsService.markAsSeen(notification.id);
        } catch (error) {
          console.error('Error marking notification as seen:', error);
        }
      }

      this.$refs.popover.hide();

      if (notification.content_type === 'FeedbackFile') {
        // Check if user has access to this notification
        if (notification.accessible === false) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Acces restricționat',
            detail:
              'Nu ai acces la acest subiect. Cursul asociat nu este activ.',
            life: 3000,
          });
          return;
        }
        this.navigateToSubject(notification.subject_id);
      } else if (notification.content_type === 'ExamAllocated') {
        if (notification.subject_id) {
          this.navigateToSubject(notification.subject_id);
        }
      }
    },
    async navigateToSubject(subjectId) {
      this.$inertia.visit(this.$routes.subject(subjectId));
    },

    isFeedbackFile(notification) {
      return notification?.content_type === 'FeedbackFile';
    },
    isTestPassed(notification) {
      return notification?.content_type === 'TestPassed';
    },
    isExamAllocated(notification) {
      return notification?.content_type === 'ExamAllocated';
    },
    feedbackParts(notification) {
      return {
        who: notification?.trigger_user_name || 'Un utilizator',
        subject: notification?.subject_name || 'un feedback',
        course: notification?.course_name || 'un curs',
      };
    },
    filesCountLabel(notification) {
      const count = notification?.content?.files_count;
      if (typeof count !== 'number') return 'fișiere';
      if (count === 1) return 'un fișier';
      return `${count} fișiere`;
    },
    fallbackMessage(notification) {
      if (typeof notification?.content === 'string')
        return notification.content;
      return 'Notificare';
    },

    formatDate(date) {
      return timeago.format(date);
    },
  },
};
</script>
